import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Users, Heart, Building2, GraduationCap, Hospital, Microscope, Shield, Activity, Brain, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Services = () => {
  const servicesSummary = [
    {
      title: "IVF & Fertility Analytics",
      icon: Heart,
      number: "01",
      summary: "Turn IVF cycle data into higher success rates and fewer cancellations. Biostatistics-led fertility analytics for UAE clinics with ESHRE and ISAR benchmarking.",
      url: "/services/ivf-fertility-analytics"
    },
    {
      title: "Healthcare Performance Analytics",
      icon: Activity,
      number: "02",
      summary: "Executive KPI dashboards and performance analytics for UAE hospitals and clinics. Track OPD flow, readmissions, revenue, and patient satisfaction in one view.",
      url: "/services/healthcare-performance-analytics"
    },
    {
      title: "Clinical Quality & Compliance Analytics",
      icon: Shield,
      number: "03",
      summary: "Accreditation-ready quality and compliance analytics for UAE healthcare. DHA, DOH, MOH, and JCI reporting with quality indicators and audit dashboards.",
      url: "/services/clinical-quality-compliance-analytics"
    },
    {
      title: "Predictive Analytics & Decision Intelligence",
      icon: Brain,
      number: "04",
      summary: "Predictive modelling and machine learning for UAE healthcare. Forecast demand, stratify patient risk, and plan capacity with defensible statistical methods.",
      url: "/services/predictive-analytics-decision-intelligence"
    },
    {
      title: "Research & Biostatistics",
      icon: GraduationCap,
      number: "05",
      summary: "End-to-end biostatistics support from study design to publication. Statistical analysis in R, SPSS, STATA, and manuscript review for researchers.",
      url: "/services/research-biostatistics"
    }
  ];

  const whatWeHelp = [
    "Reduce patient drop-out between cycles",
    "Lower cycle and procedure cancellation rates",
    "Improve consultation-to-treatment conversion",
    "Track OPD wait times and patient satisfaction",
    "Benchmark outcomes against ESHRE, ISAR, and JCI standards",
    "Prepare for DHA, DOH, and MOH accreditation and reporting",
    "Support publications and research with clean, structured data",
    "Enable confident, board-ready clinical decisions"
  ];

  const whoWeWorkWith = [
    {
      title: "IVF & Fertility Clinics",
      icon: Heart,
      subtitle: "IVF centres, fertility specialists, embryology labs",
      painPoints: [
        "Patients dropping out between cycles without explanation",
        "Cycle cancellation rates that aren't being tracked or acted on",
        "No clear picture of which protocols are driving success vs. failure",
        "Annual success rate reports that take weeks to produce manually",
        "ESHRE and ISAR international benchmarking gaps"
      ],
      metrics: "Patient retention rate · Cycle cancellation rate · Clinical pregnancy rate · Live birth rate · Protocol-level success comparison · Consultation-to-cycle conversion"
    },
    {
      title: "Hospitals & Multi-Specialty Centres",
      icon: Building2,
      subtitle: "Hospitals, department heads, healthcare administrators, COOs",
      painPoints: [
        "No visibility into department-level KPIs in a single view",
        "Readmission and complication rates that aren't being benchmarked",
        "Accreditation cycles (DHA, DOH, JCI) requiring data they don't have structured",
        "Length of stay and bed occupancy data sitting unused",
        "Research data that never makes it to publication"
      ],
      metrics: "Readmission rate · Length of stay · Bed occupancy · Department KPI trends · Complication rate · Accreditation readiness score"
    },
    {
      title: "Polyclinics & Specialty Clinics",
      icon: Hospital,
      subtitle: "Polyclinic managers, specialty clinic owners, medical directors",
      painPoints: [
        "OPD wait times affecting patient satisfaction and retention",
        "No tracking of consultation-to-procedure conversion",
        "Patient satisfaction data collected but never analysed",
        "DHA/DOH compliance reporting done manually and inconsistently",
        "Growth decisions made on gut feel rather than operational data"
      ],
      metrics: "OPD wait time · Patient satisfaction score · Consultation-to-procedure conversion · No-show and cancellation rate · Revenue per patient visit"
    },
    {
      title: "Healthcare Startups & Research Teams",
      icon: Microscope,
      subtitle: "Healthcare founders, clinical researchers, PhD students, academic institutions",
      painPoints: [
        "Investor decks that need credible outcome data, not just projections",
        "Research datasets that are messy, incomplete, or poorly structured",
        "Study designs that won't survive peer review",
        "Statistical analysis done without domain expertise",
        "Publication timelines at risk due to methodology gaps"
      ],
      metrics: "Study power and sample size · Statistical methodology robustness · Peer review revision cycles · Research timeline adherence"
    }
  ];

  const valueProposition = [
    {
      title: "Patient Retention",
      description: "For a typical mid-size UAE clinic, retaining 2–3 additional patients per month from drop-out prevention can recover AED 30,000–60,000+ annually (illustrative figures based on typical IVF pricing)."
    },
    {
      title: "Cycle Recovery",
      description: "For a typical mid-size UAE clinic running 200 cycles annually, a 5% reduction in cycle cancellation rate recovers 10 cycles and their associated revenue per year."
    },
    {
      title: "Conversion Uplift",
      description: "A 5% improvement in consultation-to-treatment conversion compounds across every new patient enquiry your clinic receives, with measurable revenue impact within months."
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Healthcare Analytics & Biostatistics Services | StatZen</title>
        <meta name="description" content="Biostatistics and analytics services for UAE fertility clinics, hospitals, and research teams. Explore StatZen's five core services. PDPL and DoH aligned." />
        <link rel="canonical" href="https://statzenanalytics.com/services" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Services
            </h1>
            <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6">
              Data-Driven Biostatistics & Healthcare Analytics
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-4">
              At StatZen Analytics, we turn your clinical and operational data into decisions that protect revenue, improve outcomes, and build the patient trust that drives growth.
            </p>
            <p className="text-base text-gray-600 max-w-4xl mx-auto">
              We work with fertility clinics, hospitals, specialty clinics, polyclinics, and research teams across the UAE, aligned with DHA, DOH, MOH, JCI, and international healthcare frameworks.
            </p>
          </div>
        </div>
      </section>

      {/* What We Help You Achieve */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What We Help You Achieve
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {whatWeHelp.map((item, index) => (
              <div key={index} className="flex items-start space-x-3 bg-blue-50 p-4 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who We Work With
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We tailor our approach to the specific performance metrics and regulatory environment of each client type.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whoWeWorkWith.map((client, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <client.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{client.title}</h3>
                      <p className="text-sm text-gray-600">{client.subtitle}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Key pain points we address:</h4>
                    <ul className="space-y-2">
                      {client.painPoints.map((point, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0 mt-2" />
                          <span className="text-gray-700 text-sm">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Metrics we move:</h4>
                    <p className="text-gray-700 text-sm">{client.metrics}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Core Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Five specialized services covering the full spectrum of healthcare analytics needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesSummary.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="border-gray-200 bg-white hover:shadow-xl transition-all duration-300 group">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-7 h-7 text-blue-600" />
                      </div>
                      <div className="text-2xl font-bold text-blue-600">{service.number}</div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed text-sm">
                      {service.summary}
                    </p>
                    <Link to={service.url}>
                      <button className="w-full flex items-center justify-center space-x-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors border border-blue-600 hover:bg-blue-50 px-4 py-2 rounded-lg">
                        <span>Learn More</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What This Is Worth */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What This Is Worth to Your Clinic
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Analytics is an investment. Here&apos;s what that investment typically unlocks:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {valueProposition.map((value, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md border-2 border-blue-200 hover:border-blue-400 transition-all duration-300">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-700">{value.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600 italic max-w-3xl mx-auto">
              These are illustrative figures based on typical UAE clinic sizes. Your actual numbers depend on your current baseline. which is exactly what we help you establish in your first consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Consulting Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Initial Consultation',
                description: 'We understand your objectives, current data setup, key metrics, and specific challenges.'
              },
              {
                step: '02',
                title: 'Analysis Planning',
                description: 'We design the right statistical approach for your question. no generic templates.'
              },
              {
                step: '03',
                title: 'Data Analysis',
                description: 'Rigorous analysis with validation checks and clear audit trails.'
              },
              {
                step: '04',
                title: 'Results & Reporting',
                description: 'Insights delivered in plain language, with specific recommendations and next steps.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center relative">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
                {/* Arrow connector */}
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-blue-300">
                    <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-l-8 border-l-blue-300"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to See What Your Data Is Telling You?
          </h2>
          <p className="text-lg text-blue-50 mb-4">
            Whether you run an IVF clinic, a polyclinic, a hospital department, or a research programme. if you collect clinical data, there are insights in it that your team isn&apos;t yet acting on.
          </p>
          <p className="text-lg text-blue-50 mb-8">
            Book a free 30-minute consultation. We&apos;ll look at your specific context, identify where the gaps are, and tell you exactly what we&apos;d do about them.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300">
                Book a Free Consultation
              </button>
            </Link>
            <Link to="/contact">
              <button className="bg-teal-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors duration-300">
                Discuss Your Project
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
